import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import type { TopBarBreadcrumb } from "./TopBar";
import { useIsMobileSidebar } from "./useSidebarViewport";

const DESKTOP_SIDEBAR_ID = "app-sidebar-desktop";

const MobileNavigation = ({
  title,
  breadcrumbs,
  actions,
  direction,
}: {
  title?: string;
  breadcrumbs?: TopBarBreadcrumb[];
  actions?: React.ReactNode;
  direction: "ltr" | "rtl";
}) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
      <TopBar
        title={title}
        breadcrumbs={breadcrumbs}
        collapsed={!mobileOpen}
        sidebarTrigger
        onToggleSidebar={() => {}}
        actions={actions}
      />
      <SheetContent
        side="inline-start"
        dir={direction}
        showCloseButton={false}
        className="w-64 border-none p-0"
      >
        <SheetTitle className="sr-only">
          {t("sidebar.navigation")}
        </SheetTitle>
        <SheetDescription className="sr-only">
          {t("sidebar.navigationDescription")}
        </SheetDescription>
        <AppSidebar
          collapsed={false}
          surface="mobile"
          onNavigate={() => setMobileOpen(false)}
        />
      </SheetContent>
    </Sheet>
  );
};

/**
 * The application shell for migrated screens.
 *
 * This deliberately does NOT replace components/DashboardLayout.jsx. That one
 * still serves the 20 legacy pages and depends on react-router hooks, which throw
 * outside the legacy <Router>. The two coexist until every screen has moved, the
 * same strangler arrangement as the router itself.
 *
 * Navigation here goes through TanStack Router, which owns the root, so links
 * work whether the destination is a migrated route or a legacy one rendered by
 * the catch-all.
 */
export const AppShell = ({
  title,
  breadcrumbs,
  actions,
  contentWidth = "contained",
  children,
}: {
  title?: string;
  breadcrumbs?: TopBarBreadcrumb[];
  actions?: React.ReactNode;
  /**
   * Mirrors the one layout decision DashboardLayout made for its screens.
   *
   * It wrapped children in <Container maxWidth="xl"> — 1536px, centred — unless
   * the screen passed `fullHeight`, in which case it did not.
   *
   * "contained" is the default because that is the overwhelming majority: of the
   * 21 screens, 18 were contained and 3 (Flows, ChatPlayground, Playground) were
   * full-bleed. Defaulting the other way meant 18 migrations each had to remember
   * to opt in, and forgetting is invisible until someone opens the app on a wide
   * monitor — MCPs shipped that way and nobody noticed.
   *
   * So: pass contentWidth="full" only when the legacy DashboardLayout call passed
   * `fullHeight`, or when the design calls for an edge-to-edge screen (MCPs).
   *
   * One consequence worth knowing: "contained" wraps children in a div of
   * automatic height, so a page that lays itself out with `h-full` — a two-pane
   * split, a canvas — collapses inside it. Those screens want "full" anyway.
   */
  contentWidth?: "full" | "contained";
  children: React.ReactNode;
}) => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobileSidebar();
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const direction =
    (typeof document !== "undefined" &&
      document.documentElement.dir === "rtl") ||
    i18n.dir() === "rtl"
      ? "rtl"
      : "ltr";

  const toggleDesktopSidebar = () =>
    setDesktopCollapsed((collapsed) => !collapsed);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-sidebar text-foreground">
      {!isMobile && (
        <AppSidebar
          id={DESKTOP_SIDEBAR_ID}
          collapsed={desktopCollapsed}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {isMobile ? (
          <MobileNavigation
            title={title}
            breadcrumbs={breadcrumbs}
            actions={actions}
            direction={direction}
          />
        ) : (
          <TopBar
            title={title}
            breadcrumbs={breadcrumbs}
            collapsed={desktopCollapsed}
            sidebarControls={DESKTOP_SIDEBAR_ID}
            onToggleSidebar={toggleDesktopSidebar}
            actions={actions}
          />
        )}
        <main className="min-h-0 flex-1 overflow-auto">
          {contentWidth === "contained" ? (
            <div className="mx-auto w-full max-w-screen-2xl">{children}</div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
};
